import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { UserService } from '@user/user.service';
import { finalize, switchMap, take } from 'rxjs/operators';
import { User } from '@user/user.model';
import { ModalDialogComponent } from '@shared/components/modal-dialog/modal-dialog.component';
import { Project } from '@project/shared/project.model';
import { Milestone } from '../milestone.model';
import { MilestoneStatus } from '../milestone-status.model';
import { MILESTONE_TYPE } from '../milestone-type.enum';
import { Document } from '@shared/components/files/shared/models/document.model';
import { MilestoneService } from '../milestone.service';
import { DocumentDataService } from '@shared/components/files/shared/document-data.service';
import { EventService } from '@shared/event.service';
import { DateTime } from 'luxon';
import { TaskService } from 'src/app/task/task.service';
import { OesTaskType } from 'src/app/task/task-type.enum';
import { OesTask } from 'src/app/task/task.model';
import { Subscription } from 'rxjs';
import { Installer } from '@project/installers/installer.model';
import { Offtaker } from '@project/offtakers/offtaker.model';
import { MILESTONE_STATUS } from '@program/shared/milestone-status.enum';
import { MilestonePermissionsService } from '@shared/services/milestone-permissions.service';

@Component({
  selector: 'oes-milestone-view',
  templateUrl: './milestone-view.component.html',
  styleUrls: ['./milestone-view.component.scss'],
  providers: [MilestonePermissionsService]
})
export class MilestoneViewComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('deleteAttachmentWarningModal', {static: false}) deleteAttachmentWarningModal: ModalDialogComponent;
  @ViewChild('tabContainer', {static: false}) tabContainer: ElementRef;
  @ViewChildren('tabElement') tabElements!: QueryList<ElementRef>;

  @Input() index: number;
  @Input() initialStatusSelection: any;
  @Input() isProgramOwner: boolean;
  @Input() milestone: Milestone;
  @Input() milestoneForm: any;
  @Input() milestoneStatus: MilestoneStatus;
  @Input() milestoneType: MILESTONE_TYPE;
  @Input() project: Project;
  @Input() projectOfftaker: Offtaker;
  @Input() projectInstaller: Installer;
  @Input() readOnly: boolean;
  @Input() statusDropdownOptions: any[];
  @Input() statusChanged!: (event: any) => void;

  @Output() closeMilestoneView: EventEmitter<void> = new EventEmitter<void>();
  @Output() formValidOutput: EventEmitter<boolean> = new EventEmitter<boolean>();

  currentTask: OesTask;
  documentsToDisplay: Document[];
  formValid: boolean = false;
  isLoading = false;
  isProjectOwner = false;
  milestoneTypes = MILESTONE_TYPE;
  tabs = {
    form: true,
    notes: false,
    attachments: false,
  };
  user: User;

  private attachmentIndexToDelete: number;
  private refetchTaskSubscription: Subscription;

  constructor(private _documentDataService: DocumentDataService,
              private _eventService: EventService,
              private _taskService: TaskService,
              private _milestoneService: MilestoneService,
              private _userService: UserService,
              private _milestonePermissionsService: MilestonePermissionsService) {
  };

  ngOnInit() {
    this._userService.getCurrentUser()
      .pipe(take(1))
      .subscribe((user: User) => {
        this.user = user;
      });
    const projectPermissions = this.project?.projectPermissions;
    this.readOnly = projectPermissions?.project?.modulePermissions?.milestones?.readOnly;
    this.isProgramOwner = projectPermissions?.currentUser?.programOwner;
    this.isProjectOwner = projectPermissions?.currentUser?.projectOwner;
    this.documentsToDisplay = this.milestone.documents.filter(doc =>
      (doc.documentKey.docType as unknown) === 'FILE'
    );
    this.initTabSelect();
    this.findTasks();
    this.refetchTaskSubscription = this._milestoneService.refetchTask$.subscribe(() => {
      this.findTasks();
    });
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.milestone?.currentValue?.documents) {
      this.isLoading = false;
      this.documentsToDisplay = this.milestone.documents.filter(doc =>
        (doc.documentKey.docType as unknown) === 'FILE'
      );
    }
    if (changes.milestone?.currentValue?.id !== changes.milestone?.previousValue?.id) {
      this.initTabSelect();
    }
  }

  ngAfterViewInit(): void {
    this.setTabsContainerWidth();
  }

  ngOnDestroy() {
    this.refetchTaskSubscription.unsubscribe();
  }

  get canDisplayEditNameAndDescription(): boolean {
    return this._milestonePermissionsService.shouldDisplayEditNameAndDescription(this.milestone, this.project, this.milestoneType);
  }

  get canEditMilestone(): boolean {
    return this._milestonePermissionsService.canEditMilestone(this.milestone, this.project);
  }

  setFormValid(validity: boolean) {
    this.formValid = validity;
    this.formValidOutput.emit(validity);
  }

  autoUpdateMilestoneStatus(status: MILESTONE_STATUS) {
    if (this.milestone.milestoneConfig.allowStatusUpdateByForm) {
      // This is to reflect the initialStatusSelection object structure
      this.initialStatusSelection = [Object.values(MILESTONE_STATUS).map((value, index) => ({
        id: index,
        name: value,
        value
      })).find(s => s.value === status)];
    }
  }

  findTasks() {
    if (this.milestone?.milestoneConfig?.contentType === 'FORM') {
      this._taskService.findByTypeAndIndex(OesTaskType.MILESTONE, this.milestone.id)
        .pipe(take(1))
        .subscribe((tasks: OesTask[]) => {
          const orderedTasks = tasks.sort((a, b) => {
            if (a.expirationDateTime > b.expirationDateTime) {
              return -1;
            } else if (a.expirationDateTime <= b.expirationDateTime) {
              return 1;
            }
          });
          this.currentTask = orderedTasks.length ? orderedTasks[0] : null;
        });
    }
  }

  initTabSelect() {
    if (this.milestone?.milestoneConfig?.contentType === 'FORM') {
      this.tabs = {form: true, notes: false, attachments: false};
    } else {
      this.tabs = {form: false, notes: true, attachments: false};
    }
  }

  handleOpenMilestoneConfigEdit() {
    this._milestoneService.openMilestoneConfig({
      action: 'edit',
      milestoneInput: this.milestone,
      milestoneType: this.milestoneType,
      form: this.milestoneForm
    });
  }

  setTabsContainerWidth(): void {
    let maxWidth = 0;

    this.tabElements.forEach((tabEl: ElementRef) => {
      const tabWidth = tabEl.nativeElement.offsetWidth;
      if (tabWidth > maxWidth) {
        maxWidth = tabWidth;
      }
    });

    this.tabContainer.nativeElement.style.width = `${maxWidth}px`;
  }

  downloadDocumentsClick(documents: Document[]) {
    this.isLoading = true;
    const docKeys = documents.map(doc => doc.documentKey.key);
    const fileNames = [];
    const uniqueKeys = [];
    docKeys.forEach(key => {
      const fileNameKey = key.split('MILESTONE')[1];
      if (!fileNames.includes(fileNameKey)) {
        uniqueKeys.push(key);
        fileNames.push(fileNameKey);
      }
    });
    const orgId = this.project.organization.id;
    const fallbackFilename = documents.length === 1
      ? documents[0].path
      : 'Documents_' + DateTime.local().toSQLDate() + '.zip';
    this._documentDataService
      .getDownloadToken(uniqueKeys, orgId)
      .pipe(
        take(1),
        switchMap((response) =>
          this._documentDataService.downloadWithToken(orgId, response.token, fallbackFilename)
        ),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        error: (error) => {
          const message = error?.error?.error || error?.message || 'Download failed. Please try again.';
          this._eventService.error(message);
        },
      });
  }

  uploadDocumentClick(event: MouseEvent) {
    event.preventDefault();
    let input = document.createElement('input');
    input.type = 'file';
    input.onchange = _ => {
      this._milestoneService.updateFilesAdded(
        this.milestone,
        { files: Array.from(input.files) }
      );
      this.isLoading = true;
    };
    input.click();
  }

  handleOpenAttachmentDeleteWarning(event, attachmentIndexToDelete: number) {
    event.preventDefault();
    event.stopPropagation();
    this.deleteAttachmentWarningModal.show();
    this.attachmentIndexToDelete = attachmentIndexToDelete;
  }

  handleDeleteAttachment() {
    const updatedMilestone = JSON.parse(JSON.stringify(this.milestone));
    updatedMilestone.documents.splice(this.attachmentIndexToDelete, 1);
    this._milestoneService.updateMilestone(updatedMilestone);
    this.handleCloseAttachmentDeleteWarning();
  }

  handleShareMilestone() {
    this._milestoneService.openMilestoneShare(this.milestone);
  }

  handleCloseAttachmentDeleteWarning() {
    this.attachmentIndexToDelete = null;
    this.deleteAttachmentWarningModal.hide();
  }

  close() {
    this.closeMilestoneView.emit();
  }
}
